/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import * as stylex from '@stylexjs/stylex';
import { $getRoot } from 'lexical';
import type { ReactNode } from 'react';
import {
  type ForwardedRef,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useHover } from 'react-aria';
import type { InputProps, InputRenderProps } from 'react-aria-components';
import {
  type ContextValue,
  DEFAULT_SLOT,
  OverlayTriggerStateContext,
  Provider,
  useContextProps,
} from 'react-aria-components';
import { ArrowTopStrokeIcon } from '../../icons/arrow-top-stroke/arrow-top-stroke.icon';
import { MicStrokeIcon } from '../../icons/mic-stroke/mic-stroke.icon';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { CHAT_TEXT_FIELD_SLOTS, ChatTextFieldHiddenContext } from '../chat-text-field/chat-text-field.component';
import { IconButtonContext, type IconButtonProps } from '../icon-button/icon-button.component';

interface ChatTextInputRenderProps extends InputRenderProps {
  /** Whether the editor has any text content. */
  isFilled: boolean;
  defaultChildren: ReactNode;
}

interface ChatTextInputProps extends Omit<InputProps, 'className' | 'style' | 'children'> {
  /** The size variant. Inherited from ChatTextField when not set explicitly. (Default: 52) */
  size?: keyof typeof sizeVariants;
  /** Whether the input is fully rounded. Inherited from ChatTextField when not set. (Default: false) */
  isRounded?: boolean;
  /** Whether the input is disabled. Propagated from a parent ChatTextField. */
  isDisabled?: boolean;
  /** Whether the input is invalid. Propagated from a parent ChatTextField. */
  isInvalid?: boolean;
  /** Whether the input is read-only. Propagated from a parent ChatTextField. */
  isReadOnly?: boolean;
  style?: StyleOrFunction<ChatTextInputRenderProps>;
  children?: (values: ChatTextInputRenderProps) => ReactNode;
}

const ChatTextInputContext = createContext<ContextValue<ChatTextInputProps, HTMLDivElement>>({});

const ChatTextInput = forwardRef((props: ChatTextInputProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, ChatTextInputContext);
  const {
    size = 52,
    isRounded = false,
    isDisabled = false,
    isInvalid = false,
    isReadOnly = false,
    children,
    placeholder = 'Message',
    style,
  } = props;

  const isHidden = useContext(ChatTextFieldHiddenContext);
  const [editor] = useLexicalComposerContext();
  const { hoverProps, isHovered } = useHover({ isDisabled });

  // Track focus within the container, including portalled popover overlays.
  const overlayTriggerState = useContext(OverlayTriggerStateContext);
  const overlayIsOpenRef = useRef(overlayTriggerState?.isOpen ?? false);
  useEffect(() => {
    overlayIsOpenRef.current = overlayTriggerState?.isOpen ?? false;
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

  const [isFocusedRaw, setIsFocusedRaw] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onFocusIn = () => setIsFocusedRaw(true);
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (!el.contains(next) && !overlayIsOpenRef.current) setIsFocusedRaw(false);
    };
    el.addEventListener('focusin', onFocusIn);
    el.addEventListener('focusout', onFocusOut);
    return () => {
      el.removeEventListener('focusin', onFocusIn);
      el.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  const isFocused = isFocusedRaw && !isDisabled;
  const isFocusVisible = false;

  const [isFilled, setIsFilled] = useState(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        setIsFilled($getRoot().getTextContent().length > 0);
      });
    });
  }, [editor]);

  // Prevent FocusScope from restoring focus to the editor after popover close.
  // When RAC's FocusScope unmounts, it dispatches 'react-aria-focus-scope-restore' before calling .focus().
  // Calling preventDefault() cancels the programmatic focus restoration.
  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return;

    const onRestoreFocus = (event: Event) => {
      if (!overlayIsOpenRef.current) {
        event.preventDefault();
      }
    };

    root.addEventListener('react-aria-focus-scope-restore', onRestoreFocus);
    return () => root.removeEventListener('react-aria-focus-scope-restore', onRestoreFocus);
  }, [editor]);

  if (isHidden) return null;

  const iconButtonKey: keyof typeof iconButtonAlignStyle = isFocused ? `${size}-focused` : size;

  const defaultChildren = (
    <div
      data-focused={isFocused || undefined}
      data-invalid={isInvalid || undefined}
      {...stylex.props(contentAreaStyles.base, contentAreaSizeVariants[size])}>
      <PlainTextPlugin
        contentEditable={
          <ContentEditable
            aria-disabled={isDisabled || undefined}
            {...stylex.props(
              contentEditableStyles.base,
              contentEditableSizeVariants[size],
              isDisabled && contentEditableStyles.disabled,
            )}
          />
        }
        placeholder={
          placeholder ? (
            <div
              data-focused={isFocused || undefined}
              data-invalid={isInvalid || undefined}
              {...stylex.props(
                placeholderStyles.base,
                placeholderSizeVariants[size],
                isHovered && !isDisabled && placeholderStyles.hovered,
                isDisabled && placeholderStyles.disabled,
              )}>
              {placeholder}
            </div>
          ) : null
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
    </div>
  );

  const renderProps = { isHovered, isFocused, isFocusVisible, isDisabled, isFilled, isInvalid, defaultChildren };

  return (
    <div
      ref={containerRef}
      data-focused={isFocused || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-hovered={isHovered || undefined}
      data-disabled={isDisabled || undefined}
      data-invalid={isInvalid || undefined}
      data-readonly={isReadOnly || undefined}
      onMouseDown={(e) => {
        if (isDisabled || isReadOnly) return;
        const root = editor.getRootElement();
        const isInsideEditor = root?.contains(e.target as Node) ?? false;
        // Prevent focus theft for clicks outside ContentEditable; allow clicks inside to place caret.
        if (!isInsideEditor) {
          e.preventDefault();
        }
        if (e.target === e.currentTarget) {
          editor.focus();
        }
      }}
      {...hoverProps}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        isRounded && roundedBorderRadiusVariants[size],
        isInvalid && invalidStyles.base,
        isDisabled && disabledStyles.base,
        typeof style === 'function' ? style(renderProps) : style,
      )}>
      <Provider
        values={[
          [
            IconButtonContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  isDisabled: isDisabled || isReadOnly,
                  isRounded,
                  size: ICON_BUTTON_SIZES[size],
                  style: iconButtonAlignStyle[iconButtonKey],
                  variant: 'ghost',
                },
                [CHAT_TEXT_FIELD_SLOTS.MIC_ICON_BUTTON]: {
                  isDisabled: isDisabled || isReadOnly,
                  isRounded,
                  children: <MicStrokeIcon />,
                  size: ICON_BUTTON_SIZES[size],
                  style: iconButtonAlignStyle[iconButtonKey],
                  variant: 'ghost',
                  'aria-label': "Voice input",
                },
                [CHAT_TEXT_FIELD_SLOTS.SEND_ICON_BUTTON]: {
                  isDisabled: isDisabled || isReadOnly,
                  isRounded,
                  size: ICON_BUTTON_SIZES[size],
                  children: <ArrowTopStrokeIcon />,
                  'aria-label': "Send message",
                  style: isFilled
                    ? [iconButtonAlignStyle[iconButtonKey], sendButtonFilledStyle.base]
                    : iconButtonAlignStyle[iconButtonKey],
                  variant: isFilled && !isDisabled && !isReadOnly ? 'primary' : 'ghost',
                },
              },
            },
          ],
        ]}>
        {typeof children === 'function' ? children?.(renderProps) : children ?? defaultChildren}
      </Provider>
    </div>
  );
});

const ICON_BUTTON_SIZES: Record<keyof typeof sizeVariants, IconButtonProps['size']> = {
  40: 32,
  52: 40,
};

const styles = stylex.create({
  base: {
    borderColor: {
      default: $.border16,
      ':is([data-disabled])': $.border10,
      ':is([data-focused])': $.borderInteractive,
      ':is([data-hovered]):not([data-focused], [data-disabled], [data-invalid])': $.border24,
      ':is([data-readonly]):not([data-focused])': $.border16,
    },
    borderStyle: 'solid',
    borderWidth: {
      default: 1,
      ':is([data-focused]):not([data-disabled])': 2,
    },
    outline: 'none',
    overflow: 'hidden',
    alignItems: 'flex-end',
    backgroundColor: $.surfaceBGPrimary,
    boxSizing: 'border-box',
    columnGap: 2,
    cursor: {
      default: 'text',
      ':is([data-disabled])': 'not-allowed',
      ':is([data-readonly])': 'default',
    },
    display: 'flex',
  },
});

const invalidStyles = stylex.create({
  base: {
    borderColor: {
      default: $.borderCritical,
      ':is([data-focused])': $.borderCritical,
      ':is([data-hovered]):not([data-focused])': $.borderCritical,
    },
    borderWidth: 2,
    backgroundColor: $.surfaceCriticalSilent,
  },
});

const disabledStyles = stylex.create({
  base: {
    borderColor: $.border10,
    borderWidth: 1,
    backgroundColor: $.surfaceBGPrimary,
  },
});

const sizeVariants = stylex.create({
  40: {
    borderRadius: 8,
    maxHeight: 72,
    minHeight: 40,
    // Padding shrinks by 1px when focused to compensate for border growing 1→2px.
    // Exclude [data-invalid]: its border is already 2px, so no compensation needed.
    paddingLeft: { default: 17, ':is([data-focused]):not([data-disabled],[data-invalid])': 16 },
    paddingRight: { default: 4, ':is([data-focused]):not([data-disabled],[data-invalid])': 3 },
  },
  52: {
    borderRadius: 8,
    maxHeight: 72,
    minHeight: 52,
    paddingLeft: { default: 14, ':is([data-focused]):not([data-disabled],[data-invalid])': 13 },
    paddingRight: { default: 6, ':is([data-focused]):not([data-disabled],[data-invalid])': 5 },
  },
});

const roundedBorderRadiusVariants = stylex.create({
  40: {
    borderRadius: 20,
  },
  52: {
    borderRadius: 25,
  },
});

// Icon buttons use marginBottom to sit centered at single-line height while remaining bottom-anchored as field grows.
const iconButtonAlignStyle = stylex.create({
  40: { alignSelf: 'flex-end', marginBottom: 3 },
  '40-focused': { alignSelf: 'flex-end', marginBottom: 2 },
  52: { alignSelf: 'flex-end', marginBottom: 5 },
  '52-focused': { alignSelf: 'flex-end', marginBottom: 4 },
});

const contentAreaStyles = stylex.create({
  base: {
    flex: '1 1 0',
    // Relative positioning required for Lexical placeholder (position:absolute).
    position: 'relative',
    minWidth: 0,
  },
});

// Vertical margin on the content area centers the single-line text within the
// container without adding to the flex row height driven by the icon buttons.
// Margin is used instead of padding so the clickable/focusable area of the
// editor is not extended by dead space — clicks in the gap land on the wrapper.
// Formula: (innerHeight - lineHeight) / 2
//   size 40, border 1px: inner = 38px, line ≈ 18px → (38 - 18) / 2 = 10px
//   size 52, border 1px: inner = 50px, line = 20px → (50 - 20) / 2 = 15px
// When focused OR invalid, the outer border is 2px → inner space shrinks by 1px each side
// → reduce margin by 1px to keep text centered. data-focused and data-invalid are set
// directly on the content area div (self-selectors StyleX compiles to .class:is([data-attr])).
const contentAreaSizeVariants = stylex.create({
  40: {
    marginBottom: { default: 10, ':is([data-focused],[data-invalid])': 9 },
    marginTop: { default: 10, ':is([data-focused],[data-invalid])': 9 },
  },
  52: {
    marginBottom: { default: 15, ':is([data-focused],[data-invalid])': 14 },
    marginTop: { default: 15, ':is([data-focused],[data-invalid])': 14 },
  },
});

const contentEditableStyles = stylex.create({
  base: {
    borderStyle: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    caretColor: $.textInteractive,
    color: $.textPrimary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontStyle: 'normal',
    fontWeight: 400,
    overflowWrap: 'break-word',
    minHeight: '1lh',
    minWidth: 0,
    overflowY: 'auto',
    width: '100%',
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
  },
  disabled: {
    caretColor: 'transparent',
    color: $.textDisabled,
    pointerEvents: 'none',
    userSelect: 'none',
  },
});

const contentEditableSizeVariants = stylex.create({
  40: {
    fontSize: 14,
    letterSpacing: -0.45,
    lineHeight: '1.286em',
    maxHeight: 50,
  },
  52: {
    fontSize: 16,
    letterSpacing: -0.5,
    lineHeight: '1.25em',
    maxHeight: 40,
  },
});

const placeholderStyles = stylex.create({
  base: {
    color: $.textPlaceholder,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontStyle: 'normal',
    fontWeight: 400,
    overflowWrap: 'break-word',
    pointerEvents: 'none',
    position: 'absolute',
    userSelect: 'none',
    left: 0,
    width: '100%',
  },
  hovered: {
    color: $.textQuaternary,
  },
  disabled: {
    color: $.textDisabled,
  },
});

// The placeholder is position:absolute inside the content area div.
// The content area's marginTop already handles vertical centering and border
// compensation, so the placeholder sits at top:0 of that div in all states.
const placeholderSizeVariants = stylex.create({
  40: {
    fontSize: 14,
    letterSpacing: -0.45,
    lineHeight: '1.286em',
    top: 0,
  },
  52: {
    fontSize: 16,
    letterSpacing: -0.5,
    lineHeight: '1.25em',
    top: 0,
  },
});

const sendButtonFilledStyle = stylex.create({
  base: {
    boxShadow: $.shadowBottom020Medium,
  },
});

export { ChatTextInput, ChatTextInputContext, iconButtonAlignStyle, type ChatTextInputProps };
